import React, { SyntheticEvent, useEffect, useState, useRef, useCallback } from "react";
import { Link as RouterLink, useHistory, useParams } from "react-router-dom";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import {
    AlertTypes,
    apiClient,
    BaseCrudClient,
    CustomDatePicker,
    enumNamesToSelect,
    GenericFormContainer,
    Grid,
    IAlertData,
    Leap4Breadcrumbs,
    Mode,
    SelectForObject,
    setAlert,
    Spinner,
    Box,
    Button,
    Divider,
    FormControl,
    LinearProgress,
    Paper,
    TextField,
    Typography,
    MuiIcons
} from "@leap4/frontend";
import { styles } from "./style";
import DialogUpdateDataPointCred from "../DialogUpdateDataPointCred/DialogUpdateDataPointCred";
import {CredentialsType, DataPointType, IAdServer, IDataPoint, ServiceFeeBy} from "@leap4/entities-discrepancy-common";
import {adServersBaseUrl, baseBackendRoute, dataPointsUrl, rescrapeUrl, rescrapeStatusUrl} from "../../../../config/generals";
import moment from "moment";
import {CredentialsForm} from "../CredentialsForm";
import {urlService} from "../../../routing/urlsService";
import {RootState} from "../../../../interfaces/root-state.interface";
import {Server} from "@leap4/auth";
import {OneOrgOnly} from "../../../shared/OneOrgOnly/OneOrgOnly";

const {Toc: TocIcon, Create: CreateIcon, AutoFixNormal: AutoFixNormalIcon, GpsFixed: GpsFixedIcon, ReportProblem: ReportProblemIcon, Refresh: RefreshIcon} = MuiIcons;

const {add, update, deleteInstance, getById} = BaseCrudClient;

interface IFormData extends Partial<IDataPoint> {
}

const mapStateToProps = (state: RootState) => ({
    selectedOrganization: state.auth.selectedOrganizationId
});

const mapActions = {
    setAlert
}

const refreshableAdServers = ["adtelligent", "pubmatic"]

const connector = connect(mapStateToProps, mapActions);

type PropsFromRedux = ConnectedProps<typeof connector>;

export interface IPropsAddEditDataPointPage extends PropsFromRedux {
    setAlert: (alert: IAlertData) => void
}

const DataPointTypeSelect = enumNamesToSelect(DataPointType, undefined, undefined, 'Type');
const ServiceFeeBySelect = enumNamesToSelect(ServiceFeeBy, undefined, undefined, 'Service fee by');

interface IRescrapeJob {
    jobId: string;
    status: 'pending' | 'running' | 'completed' | 'failed';
    progress?: {
        current: number;
        total: number;
        percentage: number;
        currentDate?: string;
    };
    results?: Array<{ date: string; status: string; error?: string }>;
    summary?: {
        successful: number;
        failed: number;
    };
}

const AddEditDataPoint: React.FC<IPropsAddEditDataPointPage> = ({ setAlert, selectedOrganization }): JSX.Element => {
    const history = useHistory();
    const params = useParams<{ id?: string }>();
    const dispatch = useDispatch();

    const schemaName = "data-point";
    const addInstanceRoute = `/${schemaName}s/add`;
    const editInstanceRoute = `/${schemaName}s/`;

    const [isAddPage, setIsAddPage] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [adServers, setAdServers] = useState<Array<IAdServer>>([]);

    const [dataPoint, setDataPoint] = useState<IFormData>({});

    // Rescrape state
    const [rescrapeStartDate, setRescrapeStartDate] = useState<Date>(moment().subtract(7, 'days').toDate());
    const [rescrapeEndDate, setRescrapeEndDate] = useState<Date>(moment().subtract(1, 'days').toDate());
    const [rescrapeJob, setRescrapeJob] = useState<IRescrapeJob | null>(null);
    const [isRescraping, setIsRescraping] = useState<boolean>(false);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    const pollRescrapeStatus = useCallback(async (jobId: string) => {
        try {
            const response = await apiClient.get(`${rescrapeStatusUrl}/${jobId}`);
            const jobData = response.data;
            setRescrapeJob(jobData);

            if (jobData.status === 'completed' || jobData.status === 'failed') {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                setIsRescraping(false);

                if (jobData.status === 'completed') {
                    setAlert({
                        msg: `Rescrape completed! ${jobData.summary?.successful || 0} succeeded, ${jobData.summary?.failed || 0} failed`,
                        type: jobData.summary?.failed > 0 ? AlertTypes.WARNING : AlertTypes.SUCCESS,
                        d: dispatch
                    });
                } else {
                    setAlert({ msg: "Rescrape job failed", type: AlertTypes.FAILURE, d: dispatch });
                }
            }
        } catch (error) {
            console.error('Failed to poll rescrape status:', error);
        }
    }, [dispatch, setAlert]);

    const startRescrape = async () => {
        if (!params.id) return;

        try {
            setIsRescraping(true);
            const response = await apiClient.post(rescrapeUrl, {
                startDate: moment(rescrapeStartDate).format('YYYY-MM-DD'),
                endDate: moment(rescrapeEndDate).format('YYYY-MM-DD'),
                dataPointId: params.id
            });

            const jobData = response.data;
            setRescrapeJob(jobData);

            if (jobData.jobId) {
                // Start polling
                pollingIntervalRef.current = setInterval(() => {
                    pollRescrapeStatus(jobData.jobId);
                }, 2000);
            }

            setAlert({ msg: "Rescrape job started", type: AlertTypes.SUCCESS, d: dispatch });
        } catch (error: any) {
            // Handle 409 Conflict - job already running
            if (error.response?.status === 409) {
                const errorData = error.response?.data;
                const existingJobId = errorData?.jobId;

                setAlert({
                    msg: "A rescrape job is already in progress for this data point",
                    type: AlertTypes.WARNING,
                    d: dispatch
                });

                // If we have the existing job info, show it and start polling
                if (existingJobId) {
                    setRescrapeJob({
                        jobId: existingJobId,
                        status: errorData?.status || 'running',
                        progress: errorData?.progress
                    });
                    setIsRescraping(true);

                    // Start polling the existing job
                    pollingIntervalRef.current = setInterval(() => {
                        pollRescrapeStatus(existingJobId);
                    }, 2000);
                } else {
                    setIsRescraping(false);
                }
            } else {
                setIsRescraping(false);
                const serverMsg = error.response?.data?.message;
                const msg = typeof serverMsg === 'string'
                    ? serverMsg
                    : Array.isArray(serverMsg)
                        ? serverMsg.join(' ')
                        : 'Failed to start rescrape';
                setAlert({ msg, type: AlertTypes.FAILURE, d: dispatch });
                console.error('Failed to start rescrape:', error);
            }
        }
    };

    const handleFormChange = (e: any) => {
        let { name, value } = e.target;
        setDataPoint({ ...dataPoint, [name]: value });
    }

    const refreshAccounts = async () => {
        if (!dataPoint) {
            return;
        }

        const baseUrl = `${dataPointsUrl}/refresh-multi/${dataPoint._id.toString()}`;
        try {
            setIsLoading(true)
            await apiClient.get(baseUrl)
        } catch (e) {
            setAlert({ msg: "Couldnt Refresh Tokens", type: AlertTypes.FAILURE, d: dispatch })
        } finally {
            setIsLoading(false)
        }
    }

    const handleCredentialsPropChange = (e: any) => {
        let { name, value } = e.target;
        if (name === 'type') {
            setDataPoint({ ...dataPoint, credentials: { customData: {}, type: value } }) // if type is change, I want to erase all previous data
            return;
        }
        if (e.target?.attributes?.customData) {
            name = e.target.attributes.customData.value;
            const customData = { ...dataPoint.credentials.customData, [name]: value }
            setDataPoint({ ...dataPoint, credentials: { ...dataPoint.credentials, customData } })
            return;
        }
        setDataPoint({ ...dataPoint, credentials: { ...dataPoint.credentials, [name]: value } as any })

    }


    const handleSubmit = async (ev: SyntheticEvent) => {
        // For block rendering.
        ev?.preventDefault();
        if (!dataPoint) {
            return;
        }
        let finalDatapoint
        const adServerName = adServers.find(ads => ads._id.toString() === dataPoint.adServer.toString())?.name
        switch (adServerName) {
            case "teads":
            case "publica": {
                finalDatapoint = {
                    ...dataPoint,
                    credentials: { ...dataPoint.credentials, type: CredentialsType.PUBLICA }
                }
                break
            }
            case "freewheel": {
                finalDatapoint = {
                    ...dataPoint,
                    credentials: { ...dataPoint.credentials, type: CredentialsType.TOKEN }
                }
            }
        }

        setIsLoading(true)
        if (isAddPage) {
            //TODO: Replace with dto type instead of any.
            await add<any>(finalDatapoint || dataPoint, schemaName, history, dispatch, baseBackendRoute);
        } else if (params.id && params.id != "add") {
            //TODO: Replace with dto type instead of any.
            await update<any>(params.id, finalDatapoint || dataPoint, schemaName, history, dispatch, baseBackendRoute);
        }
        setAlert({ msg: "Saved Succesfully", type: AlertTypes.SUCCESS })
        setIsLoading(false)
    };


    // Init form.
    useEffect(() => {
        (async () => {
            setIsLoading(true);

            const id = params.id;

            // TODO: Bad code, replace it with generic apiClient.
            let adServersData: IAdServer[] = await (await apiClient.get(adServersBaseUrl)).data.results;

            // For editUser page.
            if (id && id !== "add") {
                setIsAddPage(false);
                const instanceData = await getById(id, schemaName, dispatch, baseBackendRoute);
                setDataPoint({ ...instanceData });
            } else {
                setDataPoint({
                    name: "",
                    type: DataPointType.SERVER,
                    adServer: adServersData[0]._id,
                    serviceFee: 0,
                    serviceFeeBy: ServiceFeeBy.REQUESTS,
                    credentials: { type: CredentialsType.PASSWORD, customData: {} }
                })
            }

            setAdServers(adServersData);
            setIsLoading(false);
        })()
    }, [selectedOrganization]);

    const deleteFunction = async () => {
        setIsLoading(true)
        await deleteInstance(String(params.id), schemaName, history, dispatch, baseBackendRoute)
        setIsLoading(false);
    }

    useEffect(() => {
        // if adServer===publcia id, change the credentials type to publica
        if (dataPoint.adServer === '62b1c1a2578e97a4daa5c35a' && dataPoint.credentials.type !== CredentialsType.PUBLICA) setDataPoint({
            ...dataPoint,
            credentials: { type: CredentialsType.PUBLICA }
        });
    }, [dataPoint.adServer]);

    const getLinkForOverview = (): string => {
        let urlToReturn = ''
        switch (dataPoint.type) {
            case DataPointType.SERVER:
                urlToReturn = urlService.dataPointOverview(params.id, true)
                break;
            case DataPointType.PUBLISHER_LOGIN:
                urlToReturn = urlService.publisherDataPoint(params.id, true)
                break;
        }
        return urlToReturn
    }
    const isRefreshable = refreshableAdServers.some(adServerName => (adServers.find(x => x._id?.toString() === dataPoint.adServer?.toString()))?.name === adServerName)

    const schemaNameUpperCase = schemaName[0].toUpperCase() + schemaName.slice(1)

    const lastChosenOrganization = localStorage.getItem(Server.headers.X_ORGANZIZATION).split('-')
    if (lastChosenOrganization.length > 1) {
        return <OneOrgOnly />
    }

    return (
        <>
            <Leap4Breadcrumbs
                breadcrumbs={[
                    {
                        text: "DataPoints Dashboard",
                        icon: <TocIcon />,
                        link: urlService.dataPoints()
                    },
                    {
                        text: isAddPage ? "Create" : "Update",
                        link: isAddPage ? addInstanceRoute : editInstanceRoute,
                        icon: isAddPage ? <CreateIcon /> : <AutoFixNormalIcon />
                    },
                    {
                        text: dataPoint.name,
                        link: "",
                        icon: <GpsFixedIcon />
                    }
                ]}
            />
            <GenericFormContainer
                disabled={isLoading}
                onDeleteFunction={deleteFunction} mode={isAddPage ? Mode.Create : Mode.Edit}
                onCreateOrEditFunc={handleSubmit}>


                {isLoading ? (
                    <Grid style={{ height: 300, }}>
                        <Spinner />
                    </Grid>
                ) : (
                    <form style={styles.form} noValidate onSubmit={handleSubmit}>
                        <Grid container gap={"20px"} direction={"column"} alignItems={"flex-start"}>
                            <Grid container justifyContent={"space-between"} spacing={{ lg: 3, xs: 1 }}
                                flexDirection={'row'} width={"100%"} flexWrap={'nowrap'}>
                                <Grid item width={"max-content"} lg={5}>
                                    <Typography fontFamily={"Quicksand"} fontWeight={600} variant="h5"
                                        textAlign={'left'}>
                                        DataPoint Information
                                    </Typography>
                                </Grid>
                                <Grid container flexDirection={'row'} gap={3} flexWrap={'nowrap'}
                                    justifyContent={'flex-end'} alignItems={'center'}>
                                    {(params.id && isRefreshable) &&
                                        <Grid item>
                                            <Button onClick={refreshAccounts} size="large" variant="contained"
                                                color="primary">
                                                Refresh Logins
                                            </Button>
                                        </Grid>}
                                    {params.id &&
                                        <Grid item>
                                            <RouterLink to={getLinkForOverview()}>
                                                <Button size="large" variant="contained" color="primary">
                                                    To Overview
                                                </Button>
                                            </RouterLink>
                                        </Grid>}

                                </Grid>
                            </Grid>
                            {dataPoint?._id && <Grid item lg={12} width={"100%"}>
                                <TextField fullWidth label={"Datapoint ID"} disabled value={dataPoint._id} />
                            </Grid>}

                            <Grid item lg={6} sm={12} width={"100%"}>
                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="Name"
                                    name="name"
                                    autoComplete="name"
                                    autoFocus
                                    onChange={handleFormChange}
                                    value={dataPoint?.name}
                                />
                            </Grid>
                            <Grid container justifyContent={"flex-start"} spacing={{ lg: 2, xs: 2 }} sx={{ marginBottom: { xs: 1, lg: 1 } }}>
                                <Grid>

                                    <FormControl variant="outlined" style={{...styles.formControl, minWidth: '200px'}}>
                                        <DataPointTypeSelect
                                            label={'Type'}
                                            required
                                            fullWidth
                                            value={dataPoint.type}
                                            onChange={handleFormChange}
                                            name="type" />
                                    </FormControl>

                                </Grid>

                                <Grid>
                                    <FormControl variant="outlined" style={{...styles.formControl, minWidth: '200px'}}>
                                        <SelectForObject title="Ad Server" items={adServers}
                                            label={"Ad Server"}
                                            valueKey={"_id"} labelKey={"name"} name={"adServer"}
                                            value={dataPoint.adServer}
                                            onChange={handleFormChange} />
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <Grid container justifyContent={"flex-start"} spacing={{ lg: 2, xs: 2 }} sx={{ marginTop: { xs: 1, lg: 1 } }}>
                                <Grid>
                                    <FormControl variant="outlined" style={{...styles.formControl, minWidth: '200px'}}>
                                        <ServiceFeeBySelect
                                            label={'Service Fee By'}
                                            required
                                            fullWidth
                                            value={dataPoint.serviceFeeBy}
                                            onChange={handleFormChange}
                                            name="serviceFeeBy" />
                                    </FormControl>
                                </Grid>
                                <Grid>
                                    <FormControl variant="outlined" style={{...styles.formControl, minWidth: '200px'}}>
                                        <TextField
                                            variant="outlined"
                                            margin="normal"
                                            required
                                            fullWidth
                                            type={"number"}
                                            label={dataPoint.serviceFeeBy === ServiceFeeBy.REV_PERCENT ? "Service Fee from Rev (%)" : "Service Fee (CPM)"}
                                            name="serviceFee"
                                            onChange={handleFormChange}
                                            value={dataPoint?.serviceFee}
                                            style={{ marginTop: 0 }}
                                        />
                                    </FormControl>
                                </Grid>

                            </Grid>

                            {(!isAddPage && params.id && !isRefreshable) &&
                                <Grid>
                                    <DialogUpdateDataPointCred id={params.id} />
                                </Grid>
                            }

                            {
                                // Credentials fields (We want to display this fields only in Add mode).
                                (isAddPage && dataPoint.credentials && dataPoint.adServer) &&
                                <>
                                    <CredentialsForm credentials={dataPoint.credentials} adServerName={adServers?.find(a => a._id.toString() === dataPoint?.adServer.toString())?.name}
                                        handleCredentialsPropChange={handleCredentialsPropChange} />
                                </>

                            }

                            {/* Rescrape Section - Only show on edit page */}
                            {!isAddPage && params.id && (
                                <Grid item width="100%" marginTop={4}>
                                    <Divider sx={{ marginBottom: 3 }} />
                                    <Paper elevation={2} sx={{ padding: 3, borderRadius: 2 }}>
                                        <Typography fontFamily="Quicksand" fontWeight={600} variant="h6" marginBottom={2}>
                                            <RefreshIcon sx={{ verticalAlign: 'middle', marginRight: 1 }} />
                                            Rescrape Data
                                        </Typography>

                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={4} md={3}>
                                                <CustomDatePicker
                                                    label="Start Date"
                                                    textFieldLabel="Start Date"
                                                    date={rescrapeStartDate}
                                                    setSelectedDate={(date: Date) => setRescrapeStartDate(date)}
                                                    disableFuture={true}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4} md={3}>
                                                <CustomDatePicker
                                                    label="End Date"
                                                    textFieldLabel="End Date"
                                                    date={rescrapeEndDate}
                                                    setSelectedDate={(date: Date) => setRescrapeEndDate(date)}
                                                    disableFuture={true}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4} md={3}>
                                                <Button
                                                    variant="contained"
                                                    color="secondary"
                                                    onClick={startRescrape}
                                                    disabled={isRescraping || !rescrapeStartDate || !rescrapeEndDate}
                                                    startIcon={<RefreshIcon />}
                                                    size="large"
                                                    fullWidth
                                                >
                                                    {isRescraping ? 'Rescraping...' : 'Start Rescrape'}
                                                </Button>
                                            </Grid>
                                        </Grid>

                                        {/* Progress Section */}
                                        {rescrapeJob && (
                                            <Box marginTop={3}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {rescrapeJob.status === 'pending'
                                                            ? 'Queued - waiting to start...'
                                                            : rescrapeJob.status === 'running' && rescrapeJob.progress?.currentDate
                                                                ? `Processing: ${rescrapeJob.progress.currentDate}`
                                                                : rescrapeJob.status === 'completed'
                                                                    ? 'Completed'
                                                                    : rescrapeJob.status === 'failed'
                                                                        ? 'Failed'
                                                                        : 'Starting...'}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {rescrapeJob.progress
                                                            ? `${rescrapeJob.progress.current}/${rescrapeJob.progress.total} (${rescrapeJob.progress.percentage}%)`
                                                            : '0%'}
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant={rescrapeJob.status === 'pending' ? 'indeterminate' : 'determinate'}
                                                    value={rescrapeJob.progress?.percentage || 0}
                                                    color={rescrapeJob.status === 'completed' ? 'success' : rescrapeJob.status === 'failed' ? 'error' : 'primary'}
                                                    sx={{ height: 8, borderRadius: 4 }}
                                                />

                                                {/* Summary when completed */}
                                                {rescrapeJob.status === 'completed' && rescrapeJob.summary && (
                                                    <Box marginTop={2} display="flex" gap={3}>
                                                        <Typography variant="body2" color="success.main">
                                                            Successful: {rescrapeJob.summary.successful}
                                                        </Typography>
                                                        {rescrapeJob.summary.failed > 0 && (
                                                            <Typography variant="body2" color="error.main">
                                                                Failed: {rescrapeJob.summary.failed}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>
                            )}

                        </Grid>

                    </form>


                )}
            </GenericFormContainer>


        </>
    );
}


export default connector(AddEditDataPoint);
